import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Upload, X, ImageIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

interface ProductFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: any | null;
  onSuccess: () => void;
}

export const ProductFormDialog = ({ open, onOpenChange, product, onSuccess }: ProductFormDialogProps) => {
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    tagline: "",
    description: "",
    price_25g: "",
    price_50g: "",
    stock_quantity: "0",
    is_active: true,
    is_popular: false,
    display_order: "0",
    product_image_url: "",
    lifestyle_image_url: "",
    detail_image_url: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageFiles, setImageFiles] = useState<{
    product: File | null;
    lifestyle: File | null;
    detail: File | null;
  }>({
    product: null,
    lifestyle: null,
    detail: null,
  });
  const [imagePreviewUrls, setImagePreviewUrls] = useState<{
    product: string;
    lifestyle: string;
    detail: string;
  }>({
    product: "",
    lifestyle: "",
    detail: ""
  });
  const [isUploadingImages, setIsUploadingImages] = useState(false);

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || "",
        slug: product.slug || "",
        tagline: product.tagline || "",
        description: product.description || "",
        price_25g: product.price_25g || "",
        price_50g: product.price_50g || "",
        stock_quantity: product.stock_quantity || "0",
        is_active: product.is_active ?? true,
        is_popular: product.is_popular ?? false,
        display_order: product.display_order?.toString() || "0",
        product_image_url: product.product_image_url || "",
        lifestyle_image_url: product.lifestyle_image_url || "",
        detail_image_url: product.detail_image_url || "",
      });
      setImagePreviewUrls({
        product: product.product_image_url || "",
        lifestyle: product.lifestyle_image_url || "",
        detail: product.detail_image_url || "",
      });
    } else {
      setFormData({
        name: "",
        slug: "",
        tagline: "",
        description: "",
        price_25g: "",
        price_50g: "",
        stock_quantity: "0",
        is_active: true,
        is_popular: false,
        display_order: "0",
        product_image_url: "",
        lifestyle_image_url: "",
        detail_image_url: "",
      });
      setImagePreviewUrls({
        product: "",
        lifestyle: "",
        detail: ""
      });
    }
    setImageFiles({
      product: null,
      lifestyle: null,
      detail: null,
    });
  }, [product, open]);

  const uploadImage = async (file: File, type: 'product' | 'lifestyle' | 'detail') => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${formData.slug}-${type}-${Date.now()}.${fileExt}`;
    const filePath = `${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('product-images')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('product-images')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Upload images first if they exist
      setIsUploadingImages(true);
      let productImageUrl = formData.product_image_url;
      let lifestyleImageUrl = formData.lifestyle_image_url;
      let detailImageUrl = formData.detail_image_url;

      if (imageFiles.product) {
        productImageUrl = await uploadImage(imageFiles.product, 'product');
      }
      if (imageFiles.lifestyle) {
        lifestyleImageUrl = await uploadImage(imageFiles.lifestyle, 'lifestyle');
      }
      if (imageFiles.detail) {
        detailImageUrl = await uploadImage(imageFiles.detail, 'detail');
      }
      setIsUploadingImages(false);

      const data = {
        ...formData,
        price_25g: parseFloat(formData.price_25g),
        price_50g: parseFloat(formData.price_50g),
        stock_quantity: parseInt(formData.stock_quantity),
        display_order: parseInt(formData.display_order),
        product_image_url: productImageUrl,
        lifestyle_image_url: lifestyleImageUrl,
        detail_image_url: detailImageUrl,
      };

      if (product) {
        const { error } = await supabase
          .from("products")
          .update(data)
          .eq("id", product.id);
        if (error) throw error;
        toast.success("Product bijgewerkt");
      } else {
        const { error } = await supabase.from("products").insert(data);
        if (error) throw error;
        toast.success("Product toegevoegd");
      }

      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error saving product:", error);
      toast.error(error.message || "Kon product niet opslaan");
    } finally {
      setIsSubmitting(false);
      setIsUploadingImages(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{product ? "Product Bewerken" : "Nieuw Product"}</DialogTitle>
          <DialogDescription>
            Vul de productgegevens in. Velden met * zijn verplicht.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Naam *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="slug">Slug *</Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="product-naam"
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="tagline">Tagline</Label>
            <Input
              id="tagline"
              value={formData.tagline}
              onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="description">Beschrijving</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="price_25g">Prijs 25g (€) *</Label>
              <Input
                id="price_25g"
                type="number"
                step="0.01"
                value={formData.price_25g}
                onChange={(e) => setFormData({ ...formData, price_25g: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="price_50g">Prijs 50g (€)</Label>
              <Input
                id="price_50g"
                type="number"
                step="0.01"
                value={formData.price_50g}
                onChange={(e) => setFormData({ ...formData, price_50g: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="stock_quantity">Voorraad</Label>
              <Input
                id="stock_quantity"
                type="number"
                value={formData.stock_quantity}
                onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
              />
            </div>
          </div>

          {/* Image Uploads */}
          <div className="space-y-4 border-t pt-4">
            <h3 className="font-semibold text-lg">Product Afbeeldingen</h3>
            
            {/* Product Image */}
            <div>
              <Label htmlFor="product_image">Product Foto</Label>
              <div className="flex items-center gap-4 mt-2">
                {imagePreviewUrls.product || formData.product_image_url ? (
                  <div className="relative w-32 h-32 rounded-lg overflow-hidden border">
                    <img 
                      src={imagePreviewUrls.product || formData.product_image_url} 
                      alt="Product preview"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setImageFiles({ ...imageFiles, product: null });
                        setImagePreviewUrls({ ...imagePreviewUrls, product: "" });
                        setFormData({ ...formData, product_image_url: "" });
                      }}
                      className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="w-32 h-32 border-2 border-dashed rounded-lg flex items-center justify-center">
                    <ImageIcon className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                
                <Input
                  id="product_image"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      // Validate file size (max 5MB)
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error("Bestand te groot. Max 5MB toegestaan.");
                        e.target.value = "";
                        return;
                      }
                      setImageFiles({ ...imageFiles, product: file });
                      setImagePreviewUrls({ 
                        ...imagePreviewUrls, 
                        product: URL.createObjectURL(file) 
                      });
                    }
                  }}
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                JPG, PNG of WEBP, max 5MB
              </p>
            </div>

            {/* Lifestyle Image */}
            <div>
              <Label htmlFor="lifestyle_image">Lifestyle Foto</Label>
              <div className="flex items-center gap-4 mt-2">
                {imagePreviewUrls.lifestyle || formData.lifestyle_image_url ? (
                  <div className="relative w-32 h-32 rounded-lg overflow-hidden border">
                    <img 
                      src={imagePreviewUrls.lifestyle || formData.lifestyle_image_url} 
                      alt="Lifestyle preview"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setImageFiles({ ...imageFiles, lifestyle: null });
                        setImagePreviewUrls({ ...imagePreviewUrls, lifestyle: "" });
                        setFormData({ ...formData, lifestyle_image_url: "" });
                      }}
                      className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="w-32 h-32 border-2 border-dashed rounded-lg flex items-center justify-center">
                    <ImageIcon className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                
                <Input
                  id="lifestyle_image"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error("Bestand te groot. Max 5MB toegestaan.");
                        e.target.value = "";
                        return;
                      }
                      setImageFiles({ ...imageFiles, lifestyle: file });
                      setImagePreviewUrls({ 
                        ...imagePreviewUrls, 
                        lifestyle: URL.createObjectURL(file) 
                      });
                    }
                  }}
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                JPG, PNG of WEBP, max 5MB
              </p>
            </div>

            {/* Detail Image */}
            <div>
              <Label htmlFor="detail_image">Detail Foto</Label>
              <div className="flex items-center gap-4 mt-2">
                {imagePreviewUrls.detail || formData.detail_image_url ? (
                  <div className="relative w-32 h-32 rounded-lg overflow-hidden border">
                    <img 
                      src={imagePreviewUrls.detail || formData.detail_image_url} 
                      alt="Detail preview"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setImageFiles({ ...imageFiles, detail: null });
                        setImagePreviewUrls({ ...imagePreviewUrls, detail: "" });
                        setFormData({ ...formData, detail_image_url: "" });
                      }}
                      className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="w-32 h-32 border-2 border-dashed rounded-lg flex items-center justify-center">
                    <ImageIcon className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                
                <Input
                  id="detail_image"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error("Bestand te groot. Max 5MB toegestaan.");
                        e.target.value = "";
                        return;
                      }
                      setImageFiles({ ...imageFiles, detail: file });
                      setImagePreviewUrls({ 
                        ...imagePreviewUrls, 
                        detail: URL.createObjectURL(file) 
                      });
                    }
                  }}
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                JPG, PNG of WEBP, max 5MB
              </p>
            </div>
          </div>

          {/* Display Order Field */}
          <div>
            <Label htmlFor="display_order">Display Volgorde</Label>
            <Input
              id="display_order"
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: e.target.value })}
              placeholder="0 = eerste positie"
            />
            <p className="text-sm text-muted-foreground mt-1">
              Lagere nummers verschijnen eerst (0 = bovenaan)
            </p>
          </div>

          <div className="flex items-center space-x-8">
            <div className="flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label htmlFor="is_active">Actief</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="is_popular"
                checked={formData.is_popular}
                onCheckedChange={(checked) => setFormData({ ...formData, is_popular: checked })}
              />
              <Label htmlFor="is_popular">Populair</Label>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuleren
            </Button>
            <Button type="submit" disabled={isSubmitting || isUploadingImages}>
              {isUploadingImages 
                ? "Afbeeldingen uploaden..." 
                : isSubmitting 
                  ? "Bezig..." 
                  : product 
                    ? "Bijwerken" 
                    : "Toevoegen"
              }
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
